/**
 * Snooze a person's follow-up: Tomorrow, 3 days, Next week, or a picked
 * date. The server holds the snooze until the start of that India day, and
 * ends it early when a reply from them is logged by hand or a reminder set
 * after the snooze falls due before then.
 *
 * A failed quick pick is reported through `onError`, since the menu has
 * already closed. A failed picked date keeps its dialog open with the date.
 */

import { ChevronDown, Clock, Loader2 } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSnoozeFollowUp } from "@/features/realtor/hooks/use-realtor-crm";
import { errorMessage } from "@/features/realtor/utils/error-message";
import { StudioButton } from "@/features/studio/theme";
import {
	ACTION_SECONDARY,
	addDays,
	indiaToday,
	PROMPT_CONTENT_CLASS,
	QUICK_PICKS,
} from "./follow-up-format";

export function SnoozeMenu({
	contactId,
	contactName,
	onError,
}: {
	contactId: string;
	contactName: string;
	onError: (message: string | null) => void;
}) {
	const { t } = useTranslation();
	const ids = useId();
	const snooze = useSnoozeFollowUp();
	const [picking, setPicking] = useState(false);
	const [picked, setPicked] = useState("");
	const [pickError, setPickError] = useState<string | null>(null);
	const busy = snooze.isPending;
	const tomorrow = addDays(indiaToday(), 1);

	const snoozeFor = async (days: number) => {
		onError(null);
		try {
			await snooze.mutateAsync({
				contactId,
				until: addDays(indiaToday(), days),
			});
		} catch (failure: unknown) {
			onError(errorMessage(failure, t("realtor.followUps.snooze.failed")));
		}
	};

	const snoozeUntilPicked = async () => {
		if (!picked || busy) return;
		setPickError(null);
		try {
			await snooze.mutateAsync({ contactId, until: picked });
			setPicking(false);
			setPicked("");
		} catch (failure: unknown) {
			setPickError(errorMessage(failure, t("realtor.followUps.snooze.failed")));
		}
	};

	return (
		<>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<button
						type="button"
						className={ACTION_SECONDARY}
						disabled={busy}
						aria-label={t("realtor.followUps.snooze.aria", {
							name: contactName,
						})}
					>
						{busy ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<>
								<Clock className="h-4 w-4 xl:hidden" aria-hidden />
								<span className="max-xl:sr-only">
									{t("realtor.followUps.snooze.action")}
								</span>
							</>
						)}
						<ChevronDown className="h-4 w-4 max-xl:hidden" aria-hidden />
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					{QUICK_PICKS.map((pick) => (
						<DropdownMenuItem
							key={pick.id}
							className="min-h-11"
							onSelect={() => void snoozeFor(pick.days)}
						>
							{t(`realtor.followUps.picks.${pick.id}`)}
						</DropdownMenuItem>
					))}
					<DropdownMenuItem
						className="min-h-11"
						onSelect={() => {
							onError(null);
							setPicking(true);
						}}
					>
						{t("realtor.followUps.picks.pickDate")}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			<Dialog
				open={picking}
				onOpenChange={(next) => {
					if (!next && !busy) {
						setPicking(false);
						setPickError(null);
					}
				}}
			>
				<DialogContent className={PROMPT_CONTENT_CLASS}>
					<DialogHeader>
						<DialogTitle className="font-display text-xl italic font-medium text-dashboard-text">
							{t("realtor.followUps.snooze.pickTitle", { name: contactName })}
						</DialogTitle>
						<DialogDescription className="text-sm text-dashboard-muted">
							{t("realtor.followUps.snooze.pickDescription")}
						</DialogDescription>
					</DialogHeader>
					<form
						className="flex flex-col gap-2"
						onSubmit={(event) => {
							event.preventDefault();
							void snoozeUntilPicked();
						}}
					>
						<label
							htmlFor={`${ids}-date`}
							className="text-[11px] font-semibold uppercase tracking-wider text-dashboard-muted"
						>
							{t("realtor.followUps.picks.dateLabel")}
						</label>
						<input
							id={`${ids}-date`}
							type="date"
							min={tomorrow}
							value={picked}
							onChange={(event) => setPicked(event.target.value)}
							className="h-11 rounded-lg border border-dashboard-border bg-dashboard-bg px-3 text-sm text-dashboard-text"
						/>
						<StudioButton type="submit" disabled={!picked || busy}>
							{busy && <Loader2 className="h-4 w-4 animate-spin" />}
							{t("realtor.followUps.snooze.save")}
						</StudioButton>
					</form>
					{pickError && (
						<p role="alert" className="text-sm text-dashboard-pill-risk">
							{pickError}
						</p>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}
